import { resolveContentLink } from "@/lib/contentLink";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Play, Calendar, ArrowRight, CheckCircle2, Target, MessageSquare } from "lucide-react";
import { ClientLogos } from "@/components/ClientLogos";
import { ScheduleDemo, FAQ } from "@/components/sections";
import { PartnerForChange } from "@/components/sections/PartnerForChange";
import { PageResources } from "@/components/sections/PageResources";
import { PageContainer } from "@/components/layouts/MainLayout";
import { useLocalizedImage, useLocalizedImages } from "@/hooks/use-localized-image";
import { T, THtml } from "@/components/editable/T";
import { EditableImage } from "@/components/editable/EditableImage";
import { getLocalizedRoutePath } from "@/lib/routeUtils";
import { FeatureImageFrame } from "@/components/FeatureImageFrame";

import expectationsImage from "@/assets/illustrations/performance-reviews-skills-nl.webp";
import expectationsImageEn from "@/assets/illustrations/performance-reviews-skills-en.webp";
import expectationsImageDe from "@/assets/illustrations/performance-reviews-skills-de.webp";
import feedbackImage from "@/assets/illustrations/performance-reviews-feedback.webp";
import feedbackSlackImage from "@/assets/illustrations/continu-feedback-nl.webp";
import feedbackSlackImageEn from "@/assets/illustrations/continu-feedback-en.webp";
import feedbackSlackImageDe from "@/assets/illustrations/continu-feedback-de.webp";
import qualityImage from "@/assets/illustrations/quality-conversations.png";
import autoEvaluatieImage from "@/assets/illustrations/evaluatie-auto-nl.webp";
import autoEvaluatieImageEn from "@/assets/illustrations/evaluatie-auto-en.webp";
import autoEvaluatieImageDe from "@/assets/illustrations/evaluatie-auto-de.webp";
import assistMeImage from "@/assets/illustrations/assist-me-nl.webp";
import assistMeImageEn from "@/assets/illustrations/assist-me-en.webp";
import assistMeImageDe from "@/assets/illustrations/assist-me-de.webp";
import compensationImage from "@/assets/illustrations/compensation-decisions.png";
import feedbackIntegrationImage from "@/assets/illustrations/feedback-integration.png";
import voiceSummaryImage from "@/assets/illustrations/samenvatting-nl.webp";
import voiceSummaryImageEn from "@/assets/illustrations/samenvatting-en.webp";
import voiceSummaryImageDe from "@/assets/illustrations/samenvatting-de.webp";
import expectationsScorecard from "@/assets/illustrations/expectations-scorecard.png";
import partnerHeroImage from "@/assets/images/customer-success-team-nieuw.webp";
import biasTimelineImage from "@/assets/illustrations/bias-timeline.webp";
import biasTimelineImageDe from "@/assets/illustrations/bias-timeline-de.png";
import biasTimelineImageEn from "@/assets/illustrations/bias-timeline-en.png";
import evaluatiegesprekkenHeroPhoto from "@/assets/images/evaluatiegesprekken-hero-photo.webp";

const P = "performanceReviews";

const featureColors = [
"#F3EDFD", "#FDF2ED", "#FFFBEB", "#FAEDFD", "#E8F5E9", "#F3EDFD"];


const Evaluatiegesprekken = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'nl';
  const demoPath = getLocalizedRoutePath('demo', lang);

  // Localized images from visuals DB with local fallbacks
  const heroImg = useLocalizedImage('performanceReviews', 'hero', 'hero-image', '', { preload: true });
  const biasLocalFallback = lang === 'de' ? biasTimelineImageDe : lang === 'en' ? biasTimelineImageEn : biasTimelineImage;
  const biasImg = useLocalizedImage('performanceReviews', 'biasBlock', 'bias-timeline', biasLocalFallback);
  const partnerImg = useLocalizedImage('performanceReviews', 'customerSuccess', 'partner-image', partnerHeroImage);
  const featureImages = useLocalizedImages('performanceReviews', 'features',
  ['row1-image', 'row2-image', 'row3-image', 'row4-image', 'row6-image'],
  {
    'row1-image': lang === 'de' ? expectationsImageDe : lang === 'en' ? expectationsImageEn : expectationsImage,
    'row2-image': lang === 'de' ? feedbackSlackImageDe : lang === 'en' ? feedbackSlackImageEn : feedbackSlackImage,
    'row3-image': lang === 'de' ? autoEvaluatieImageDe : lang === 'en' ? autoEvaluatieImageEn : autoEvaluatieImage,
    'row4-image': lang === 'de' ? assistMeImageDe : lang === 'en' ? assistMeImageEn : assistMeImage,
    'row6-image': lang === 'de' ? voiceSummaryImageDe : lang === 'en' ? voiceSummaryImageEn : voiceSummaryImage
  }
  );

  const features = [
  {
    titleKey: "features.row1.title",
    titleDefault: "Geef feedback vanuit heldere verwachtingen",
    bodyKey: "features.row1.body",
    bodyDefault: "Koppel evaluaties aan skills, KPI's en concreet gedrag uit het functieprofiel. Zo weten medewerkers waarop ze beoordeeld worden en hebben leidinggevenden houvast voor consistente, eerlijke gesprekken.",
    imageKey: 'row1-image',
    color: featureColors[0],
    noFrame: true,
    cta: { labelKey: "features.row1.cta", labelDefault: "Ontdek functieprofielen", link: getLocalizedRoutePath("jobProfiles", lang) }
  },
  {
    titleKey: "features.row2.title",
    titleDefault: "Verzamel feedback automatisch uit het dagelijkse werk",
    bodyKey: "features.row2.body",
    bodyDefault: "Van 1:1's tot Slack, Teams en e-mail: Learned haalt relevante input op uit de flow van het werk en zet die om in bruikbare input voor evaluaties. Zo hoef je minder achteraf te reconstrueren en mis je minder belangrijke signalen.",
    imageKey: 'row2-image',
    color: featureColors[1],
    noFrame: true,
    demoCta: true
  },
  {
    titleKey: "features.row3.title",
    titleDefault: "Laat AI de eerste versie van evaluaties voorbereiden",
    bodyKey: "features.row3.body",
    bodyDefault: "Op basis van feedback, doelen en gespreksnotities schrijft Learned een sterke concept-evaluatie. Managers hoeven niet meer vanaf nul te beginnen, maar alleen te controleren, aan te scherpen en het gesprek goed te voeren.",
    imageKey: 'row3-image',
    color: featureColors[2],
    noFrame: true,
    demoCta: true
  },
  {
    titleKey: "features.row4.title",
    titleDefault: "Geef leidinggevenden meer houvast in elk gesprek",
    bodyKey: "features.row4.body",
    bodyDefault: "Met AI-ondersteuning en praktische feedbacktips krijgen leidinggevenden meer vertrouwen in gewone én lastige gesprekken. Dat levert meer duidelijkheid, betere ontwikkeling en sterkere teams op.",
    imageKey: 'row4-image',
    color: featureColors[3],
    noFrame: true,
    demoCta: true
  },
  {
    titleKey: "features.row6.title",
    titleDefault: "Vat gesprekken in 1 klik samen met AI",
    bodyKey: "features.row6.body",
    bodyDefault: "Laat AI automatisch de belangrijkste punten uit gesprekken samenvatten, inclusief gemaakte afspraken en vervolgstappen. Zo hoeven leidinggevenden minder mee te schrijven, ligt de focus meer op het gesprek zelf en blijft opvolging niet hangen in losse notities.",
    imageKey: 'row6-image',
    noFrame: true,
    color: featureColors[5],
    demoCta: true
  }];




  return (
    <>
      {/* Hero Section */}
      <section className="pt-[140px] pb-6 px-6 relative">
        <PageContainer className="relative z-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to={`/${lang}/`} className="hover:text-primary transition-colors">
              <T page={P} k="breadcrumb.home" d="Home" />
            </Link>
            <span>›</span>
            <Link to={`/${lang}/platform/performance-management/`} className="hover:text-primary transition-colors">
              <T page={P} k="breadcrumb.parent" d="Performance Management" />
            </Link>
            <span>›</span>
            <span style={{ color: "#2A2334" }}>
              <T page={P} k="breadcrumb.current" d="Evaluatiegesprekken" />
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
            <div className="animate-fade-in">
              <T
                page={P}
                k="hero.subtitle"
                d="Onderdeel van Performance Management"
                as="span"
                className="inline-block uppercase rounded-full"
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  color: '#46315D',
                  backgroundColor: '#F3EDFD',
                  padding: '6px 14px',
                  marginBottom: '20px'
                }}
              />
              <h1 className="mb-6" style={{ color: '#2A2334' }}>
                <T page={P} k="hero.title" d="Evaluatiegesprekken die hun doel dienen" />
              </h1>
              <THtml page={P} k="hero.body" d="Ga voorbereid het gesprek in met feedback, doelen en verwachtingen die al zijn vastgelegd. Zo voer je een gesprek met concrete vervolgstappen, waar iedereen iets aan heeft." as="div" className="text-lead text-muted-foreground mb-8 max-w-xl" />
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE]" onClick={() => window.open('https://app.learned.io/register', '_blank')}>
                  <T page={P} k="hero.tryFree" d="Probeer gratis" />
                </Button>
                <Button variant="outline" size="lg" className="text-sm px-6 py-5 rounded-full bg-white hover:bg-gray-50" onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>
                  <Calendar className="mr-2 h-4 w-4" style={{ color: '#9055EE' }} />
                  <T page={P} k="hero.planDemo" d="Plan een demo" />
                </Button>
              </div>
            </div>

            <div className="animate-fade-in relative" style={{ animationDelay: '200ms' }}>
              <EditableImage
                page={P}
                section="hero"
                imageKey="hero-image"
                src={heroImg.imageUrl || evaluatiegesprekkenHeroPhoto}
                alt={heroImg.altText || "Twee collega's in gesprek tijdens een evaluatiegesprek"}
                className="w-full h-[450px] object-cover object-center rounded-2xl"
                loading="eager"
              />
              <div className="absolute -right-8 -top-5 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform rotate-3 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1100ms', animationFillMode: 'forwards' }}>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <T page={P} k="hero.cards.prepared" d="Gesprek al voorbereid" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -right-6 bottom-24 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1400ms', animationFillMode: 'forwards' }}>
                <MessageSquare className="w-4 h-4 text-[#9055EE]" />
                <T page={P} k="hero.cards.feedback" d="Feedback automatisch verzameld" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -left-6 bottom-10 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1700ms', animationFillMode: 'forwards' }}>
                <Target className="w-4 h-4 text-[#FB9A24]" />
                <T page={P} k="hero.cards.nextSteps" d="Concrete vervolgstappen" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
            </div>

          </div>
          <ClientLogos />
        </PageContainer>
      </section>

      {/* Bias Block */}
      <section className="px-6 py-0">
        <PageContainer>
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'rgb(56, 7, 67)' }}>
            <div className="p-10 lg:p-14">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start mb-10">
                <THtml page={P} k="biasBlock.title" d="Minder\nsubjectiviteit, \nbeter onderbouwde gesprekken" as="h2" className="text-white !text-3xl lg:!text-5xl !font-bold !leading-[1.4] lg:!leading-[1.4] whitespace-pre-line" />
                <div className="text-white/90 text-base lg:text-lg leading-relaxed space-y-6">
                  <THtml page={P} k="biasBlock.body1" d='Ieder mens is biased, meestal onbewust. In evaluatiegesprekken kan dat ertoe leiden dat je je iemands meest recente prestaties beter herinnert. Of dat je positievere feedback hebt voor een collega die je mag, en juist kritischer bent op een collega die je minder goed ligt.' as="p" />
                  <THtml page={P} k="biasBlock.body2" d="Maar wat als je tussentijdse feedback automatisch kunt vastleggen? Niet vlak voor een evaluatiegesprek, maar tijdens het werk, op het moment dat je iets opvalt? Daar helpt onze AI-software bij. Die herkent input voor evaluaties automatisch en vat het voor je samen." as="p" />
                </div>
              </div>
              <EditableImage page={P} section="biasBlock" imageKey="bias-timeline" src={biasImg.imageUrl} fallbackSrc={biasTimelineImage} alt={biasImg.altText || "Timeline showing automatic feedback collection"} className="hidden sm:block w-full h-auto object-contain" />
            </div>
          </div>
        </PageContainer>
      </section>

      {/* Features Section - 6 rows */}
      <section className="pt-[60px] pb-20 px-6">
        <PageContainer>
          <div className="space-y-16">
            {features.map((feature, index) => {
              const isReversed = index % 2 === 1;
              return (
                <div
                  key={index}
                  className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center animate-fade-in`}
                  style={{ animationDelay: `${index * 100}ms` }}>

                  <div className={`${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <h2 className="feature-section-heading text-foreground">
                      <T page={P} k={feature.titleKey} d={feature.titleDefault} />
                    </h2>
                    <THtml page={P} k={feature.bodyKey} d={feature.bodyDefault} as="p" className="text-lead text-muted-foreground" />

                    {(feature.cta || feature.demoCta) &&
                    <Button asChild className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white">
                        <Link to={demoPath}>
                          <T page={P} k="features.demoCta" d="Plan een demo" />
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    }
                  </div>
                  <div className={`${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    {feature.noFrame ? (
                      <EditableImage
                        page={P}
                        section="features"
                        imageKey={feature.imageKey}
                        src={featureImages[feature.imageKey]?.imageUrl || ''}
                        alt={featureImages[feature.imageKey]?.altText || ''}
                        className="w-full h-auto rounded-3xl"
                      />
                    ) : (
                      <FeatureImageFrame color={feature.color}>
                        <EditableImage
                          page={P}
                          section="features"
                          imageKey={feature.imageKey}
                          src={featureImages[feature.imageKey]?.imageUrl || ''}
                          alt={featureImages[feature.imageKey]?.altText || ''}
                          className="w-full h-full object-cover rounded-2xl"
                        />
                      </FeatureImageFrame>
                    )}
                  </div>
                </div>);

            })}
          </div>
        </PageContainer>
      </section>


      <PageResources page="evaluatiegesprekken" cards={[
        { href: resolveContentLink(`/${lang}/blog/functioneringsgesprek`, lang), image: "/images/blog/functioneringsgesprek-hero.png", alt: "Functioneringsgesprek", type: "blog", titleKey: "resources.card1.title", titleDefault: "Functioneringsgesprek nieuwe stijl: zo richt je het in op ontwikkeling" },
        { href: resolveContentLink(`/${lang}/template/functioneringsgesprek-nieuwe-stijl`, lang), image: "/images/blog/template-evaluatiegesprek-hero.avif", alt: "Evaluatiegesprek template", type: "template", titleKey: "resources.card2.title", titleDefault: "Template voor een modern functionerings- of evaluatiegesprek" },
        { href: resolveContentLink(`/${lang}/webinar/ai-in-performance-management`, lang), image: "/images/resources/ai-evaluatieproces-masterclass.webp", alt: "AI masterclass evaluatieproces", type: "webinar", titleKey: "resources.card3.title", titleDefault: "On-demand masterclass: Tijd besparen met AI in je evaluatieproces" },
        { href: resolveContentLink(`/${lang}/blog/performance-management-cyclus`, lang), image: "/images/blog/performance-management-cyclus-hero.png", alt: "Performance Management", type: "blog", titleKey: "resources.card4.title", titleDefault: "Zo pak je Performance Management slim en eerlijk aan: de gids" },
      ]} />

      <PartnerForChange />

      <ScheduleDemo />

      <FAQ
        headingClassName="feature-section-heading"
        className="mx-0 mb-[-96px] pb-[192px] rounded-none"
        style={{ background: 'linear-gradient(135deg, #2A2334 0%, #4A3660 50%, #2A2334 100%)' }}
        dark
        titleKey="performanceReviews.faq.title"
        titleDefault="Veelgestelde vragen"
        subtitleKey="performanceReviews.faq.subtitle"
        subtitleDefault="Heb je nog vragen? Hier vind je antwoorden op de meestgestelde vragen over evaluatiegesprekken."
        items={[
        {
          questionKey: "performanceReviews.faq.q1.question",
          questionDefault: "Hoe helpt Learned bij evaluatiegesprekken?",
          answerKey: "performanceReviews.faq.q1.answer",
          answerDefault: "Learned verzamelt input uit feedback, doelen en eerdere gesprekken op één plek. Daardoor gaan leidinggevenden beter voorbereid het gesprek in en kost het opstellen van evaluaties veel minder tijd."
        },
        {
          questionKey: "performanceReviews.faq.q2.question",
          questionDefault: "Hoe zorgt Learned voor eerlijkere evaluaties?",
          answerKey: "performanceReviews.faq.q2.answer",
          answerDefault: "Door evaluaties te koppelen aan duidelijke verwachtingen, skills en resultaten. Zo baseer je gesprekken minder op onderbuikgevoel en meer op concrete input die over een langere periode is opgebouwd."
        },
        {
          questionKey: "performanceReviews.faq.q3.question",
          questionDefault: "Moeten managers nog steeds zelf evaluaties schrijven?",
          answerKey: "performanceReviews.faq.q3.answer",
          answerDefault: "Ja, maar Learned maakt dat een stuk makkelijker. AI helpt met een sterke eerste opzet, zodat managers niet vanaf nul hoeven te beginnen en zich meer kunnen richten op de inhoud van het gesprek."
        },
        {
          questionKey: "performanceReviews.faq.q4.question",
          questionDefault: "Is Learned alleen geschikt voor formele beoordelingsmomenten?",
          answerKey: "performanceReviews.faq.q4.answer",
          answerDefault: "Nee, juist niet. Learned ondersteunt een doorlopend performance proces, zodat formele evaluaties niet losstaan van de dagelijkse praktijk, maar voortbouwen op feedback en gesprekken die al eerder zijn vastgelegd."
        }]
        } />

    </>);

};

export default Evaluatiegesprekken;
